import {
  computed,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  output,
} from '@angular/core';
import {
  autoUpdate,
  computePosition,
  ElementRects,
  flip,
  offset,
  Placement,
} from '@floating-ui/dom';
import { noop } from '@ps-gui/shared-utils';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NativeElementHook } from './native-element-hook.directive';

export type OffsetConfig = {
  /**
   * The provider is preferred if both the provider and hardcoded values are defined
   */
  offsetProvider?: (rects: ElementRects) => { main: number; cross: number };
  main?: number;
  cross?: number;
};

type NativeElement = HTMLElement | NativeElementHook;

/**
 * This directive utilizes @floating-ui/dom to position the host element relative
 * to some anchor element. It can be useful for things like popup-menus or dropdowns.
 * Floating-ui's `autoUpdate` feature and `flip` middleware ensures that the element
 * is visible even if the context is scrolled.
 *
 * Usage:
 *
 * ```HTML
 * <label #anchor>...</label>
 * <menu
 *    [floatingElement]
 *    [anchor]="anchor"
 *    [isOpened]="isOpened()"
 *    [placement]="'bottom'"
 *    class="absolute"
 * >
 *    ...
 * </menu>
 * ```
 */
@Directive({
  // ignore this naming rule because ctExtractionFloatingDropdown is too cumbersome
  // eslint-disable-next-line
  selector: '[floatingElement]',
})
export class FloatingElementDirective {
  private readonly element = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * A floating element is positioned relative to the anchor.
   * Check out the floating-ui documentation for more information:
   * https://floating-ui.com/docs/computeposition#anchoring
   *
   * Note that this input is NOT required since there is an alternative
   * way of providing the anchor element via {@link FloatingMenuAnchorDirective}.
   */
  readonly anchor = input<NativeElement>();

  readonly isOpened = input.required<boolean>();
  readonly placement = input<Placement>('bottom');
  readonly offsetConfig = input<OffsetConfig>();
  readonly enableAutoUpdate = input<boolean>(true);

  readonly trigger = input<NativeElement>();

  readonly flip = input<boolean>(true);

  private cleanup = noop;

  outsideClick = output();

  private readonly nativeAnchor = computed(() => this.getHTMLElement(this.anchor()));
  private readonly nativeTrigger = computed(() => this.getHTMLElement(this.trigger()));

  private readonly middlewares = computed(() => {
    const middlewares = [];

    const offsetConfig = this.offsetConfig();
    if (offsetConfig) {
      middlewares.push(
        offset(({ rects }) => {
          const providerResult = offsetConfig.offsetProvider?.(rects);

          return {
            mainAxis: providerResult?.main ?? offsetConfig.main ?? 0,
            crossAxis: providerResult?.cross ?? offsetConfig.cross ?? 0,
          };
        }),
      );
    }

    if (this.flip()) middlewares.push(flip());
    return middlewares;
  });

  constructor() {
    effect(() => {
      const anchor = this.nativeAnchor();

      if (!anchor) return;

      this.cleanup();
      if (!this.isOpened()) {
        return;
      }

      const element = this.element.nativeElement;

      const update = () =>
        computePosition(anchor, element, {
          placement: this.placement(),
          middleware: this.middlewares(),
        }).then((position) => {
          element.style.left = `${position.x}px`;
          element.style.top = `${position.y}px`;
        });

      if (this.enableAutoUpdate()) {
        this.cleanup = autoUpdate(anchor, element, update);
      } else {
        update();
        this.cleanup = noop;
      }
    });

    fromEvent(window, 'click', { capture: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (this.shouldIgnoreEvent(event)) return;

        if (!this.isOpened()) return;

        if (this.element.nativeElement.contains(event.target as HTMLElement)) return;

        this.outsideClick.emit();
      });
  }

  private shouldIgnoreEvent(event: Event): boolean {
    const trigger = this.nativeTrigger();
    if (!trigger) return false;
    return trigger.contains(event.target as HTMLElement);
  }

  private getHTMLElement<T extends NativeElement | undefined>(
    nativeElement: T,
  ): undefined extends T ? HTMLElement | undefined : HTMLElement {
    // @ts-expect-error ts does not understand the correct return type
    if (nativeElement === undefined) return undefined;
    if (this.isNativeElementHook(nativeElement)) return nativeElement.element;
    return nativeElement as HTMLElement;
  }

  private isNativeElementHook(trigger: NativeElement): trigger is NativeElementHook {
    return 'element' in trigger;
  }
}
